'use client';

/**
 * Enhanced Configuration Provider
 * Manages global application configuration
 */
import React, { ReactNode, useEffect, useState } from 'react';

import { ConfigLoader } from '@/components/common/SuspenseLoader';
import { initializeThemeRoutes } from '@/lib/legacy-theme/theme-route-initializer';
import { logger } from '@/lib/logger';

import { AppConfig, getConfigManager, loadConfigFromApi } from '../core/config/manager';

export interface EnhancedConfigProviderProps {
  children: ReactNode;
  initialConfig?: Partial<AppConfig>;
}

export const EnhancedConfigProvider: React.FC<EnhancedConfigProviderProps> = ({ children, initialConfig }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        logger.debug({ msg: 'Initializing configuration' });
        const manager = getConfigManager();

        if (initialConfig) {
          // Initialize with provided config
          await manager.initialize(initialConfig);
          logger.debug({ msg: 'Configuration initialized with provided config' });
        } else {
          // Load from API
          logger.debug({ msg: 'Loading configuration from API' });
          const config = await loadConfigFromApi();
          await manager.initialize(config);
          logger.debug({ msg: 'Configuration loaded from API', config });

          // Initialize theme routes based on active theme
          if (config.theme?.activeTheme) {
            await initializeThemeRoutes(config.theme.activeTheme);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        logger.error({ msg: 'Failed to initialize configuration', error });
        console.error('Failed to initialize configuration:', error);
        // Fallback to default config
        const defaultConfig: AppConfig = {
          app: {
            name: 'Tienda',
            description: 'E-commerce application',
            version: '1.0.0',
            environment: 'development',
          },
          theme: {
            activeTheme: 'cray26',
            mode: 'light',
            availableThemes: ['cray26'],
          },
          i18n: {
            defaultLanguage: 'en',
            availableLanguages: ['en', 'es'],
          },
          features: {
            authentication: true,
            cart: true,
            checkout: true,
            wishlist: false,
            reviews: false,
            analytics: false,
          },
          api: {
            baseUrl: process.env.NEXT_PUBLIC_API_URL || '/',
            timeout: 30000,
            retryAttempts: 3,
            version: 'v2',
          },
        };

        const manager = getConfigManager();
        await manager.initialize(defaultConfig);
        logger.debug({ msg: 'Fallback configuration initialized' });

        // Initialize theme routes for fallback config
        if (defaultConfig.theme?.activeTheme) {
          await initializeThemeRoutes(defaultConfig.theme.activeTheme);
        }

        setIsInitialized(true);
      }
    };

    initializeConfig();
  }, [initialConfig]);

  if (!isInitialized) {
    return <ConfigLoader />;
  }

  return <>{children}</>;
};
